import React, { useState, useEffect } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Users, Target, Zap, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

export default function MarketingMetrics({ leads, dateRange }) {
  const [marketingData, setMarketingData] = useState({
    cpl: 0,
    leadVolume: 0,
    conversionRate: 0,
    leadVelocity: 0,
    leadsBySource: [],
    leadVolumeOverTime: [],
    cplByChannel: [],
    funnelData: [],
    recentLeads: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMarketingData();
  }, [dateRange]);

  const loadMarketingData = async () => {
    setLoading(true);
    try {
      // DEPRECATED - leads table removed; use deals table instead
      let query = supabase
        .from('deals')
        .select('*');

      if (dateRange?.from) {
        query = query.gte('created_at', dateRange.from.toISOString());
      }
      if (dateRange?.to) {
        query = query.lte('created_at', dateRange.to.toISOString());
      }

      const { data: leadsData, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate CPL (Cost per Lead)
      const { data: campaigns } = await supabase
        .from('ad_campaigns')
        .select('budget, campaign_id');
      
      const totalBudget = campaigns?.reduce((sum, c) => sum + (c.budget || 0), 0) || 0;
      const cpl = leadsData.length > 0 ? totalBudget / leadsData.length : 0;

      // Lead volume
      const leadVolume = leadsData?.length || 0;

      // MQL -> SQL Conversion Rate
      const mqls = leadsData?.filter(l => l.lifecycle_stage === 'mql' || l.lifecycle_stage === 'sql' || l.lifecycle_stage === 'customer').length || 0;
      const sqls = leadsData?.filter(l => l.lifecycle_stage === 'sql' || l.lifecycle_stage === 'customer').length || 0;
      const conversionRate = mqls > 0 ? (sqls / mqls) * 100 : 0;

      // Lead velocity (weekly trend)
      const lastWeek = leadsData?.filter(l => {
        const createdDate = new Date(l.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return createdDate >= weekAgo;
      }).length || 0;

      const weekBefore = leadsData?.filter(l => {
        const createdDate = new Date(l.created_at);
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return createdDate >= twoWeeksAgo && createdDate < weekAgo;
      }).length || 0;

      const leadVelocity = weekBefore > 0 ? ((lastWeek - weekBefore) / weekBefore) * 100 : 0;

      // Leads by source
      const sourceCount = {};
      leadsData?.forEach(lead => {
        const source = lead.origem || 'Unknown';
        sourceCount[source] = (sourceCount[source] || 0) + 1;
      });

      const leadsBySource = Object.entries(sourceCount).map(([name, value]) => ({
        name,
        value
      }));

      // Lead volume over time (last 90 days, grouped by week)
      const volumeByWeek = {};
      leadsData?.forEach(lead => {
        const date = new Date(lead.created_at);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        const weekKey = safeFormat(weekStart, 'MMM dd');
        volumeByWeek[weekKey] = (volumeByWeek[weekKey] || 0) + 1;
      });

      const leadVolumeOverTime = Object.entries(volumeByWeek)
        .map(([date, count]) => ({ date, count }))
        .slice(-12); // Last 12 weeks

      // CPL by channel (mock data - would need campaign budget data)
      const cplByChannel = [
        { channel: 'Facebook', cpl: cpl * 0.9 },
        { channel: 'Google', cpl: cpl * 1.1 },
        { channel: 'Organic', cpl: 0 },
        { channel: 'Referral', cpl: cpl * 0.5 }
      ];

      // Funnel data
      const totalLeads = leadsData?.length || 0;
      const newLeads = leadsData?.filter(l => l.lifecycle_stage === 'new').length || 0;
      const funnelData = [
        { stage: 'Total Leads', value: totalLeads },
        { stage: 'MQL', value: mqls },
        { stage: 'SQL', value: sqls },
        { stage: 'Opportunity', value: leadsData?.filter(l => l.contact_id !== null).length || 0 }
      ];

      // Recent leads
      const recentLeads = leadsData?.slice(0, 10).map(lead => ({
        id: lead.id,
        name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'N/A',
        email: lead.primary_email || 'N/A',
        source: lead.origem || 'Unknown',
        created_at: lead.created_at,
        lifecycle_stage: lead.lifecycle_stage || 'new'
      })) || [];

      setMarketingData({
        cpl,
        leadVolume,
        conversionRate,
        leadVelocity,
        leadsBySource,
        leadVolumeOverTime,
        cplByChannel,
        funnelData,
        recentLeads
      });
    } catch (error) {
      console.error('Error loading marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, change, icon: Icon, trend }) => (
    <Card className="border-border bg-card hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="p-3 rounded-lg bg-primary/10">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          {change !== undefined && (
            <div className={`flex items-center gap-1 text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span>{Math.abs(change).toFixed(1)}%</span>
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold text-foreground">{value}</p>
        </div>
      </CardContent>
    </Card>
  );

  const getStatusBadge = (stage) => {
    const variants = {
      new: 'secondary',
      mql: 'default',
      sql: 'default',
      customer: 'default'
    };
    return <Badge variant={variants[stage] || 'secondary'}>{stage?.toUpperCase() || 'NEW'}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-12">Loading marketing data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="CPL (Cost per Lead)"
          value={`$${marketingData.cpl.toFixed(2)}`}
          change={-5.2}
          trend="down"
          icon={DollarSign}
        />
        <MetricCard
          title="Lead Volume"
          value={marketingData.leadVolume}
          change={12.3}
          trend="up"
          icon={Users}
        />
        <MetricCard
          title="MQL → SQL Conversion"
          value={`${marketingData.conversionRate.toFixed(1)}%`}
          change={8.7}
          trend="up"
          icon={Target}
        />
        <MetricCard
          title="Lead Velocity"
          value={`${marketingData.leadVelocity >= 0 ? '+' : ''}${marketingData.leadVelocity.toFixed(1)}%`}
          change={marketingData.leadVelocity}
          trend={marketingData.leadVelocity >= 0 ? 'up' : 'down'}
          icon={Zap}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Volume Over Time */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-foreground">Lead Volume - Last 90 Days</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={marketingData.leadVolumeOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--chart-1))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Leads by Source */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-foreground">Leads by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={marketingData.leadsBySource}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {marketingData.leadsBySource.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CPL by Channel */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-foreground">CPL by Channel</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={marketingData.cplByChannel}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="channel" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="cpl" fill="hsl(var(--chart-2))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-foreground">Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={marketingData.funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                <YAxis dataKey="stage" type="category" stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="value" fill="hsl(var(--chart-3))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-foreground">Leads Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {marketingData.recentLeads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell className="font-medium">{lead.name}</TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>{lead.source}</TableCell>
                  <TableCell>{getStatusBadge(lead.lifecycle_stage)}</TableCell>
                  <TableCell>{safeFormat(new Date(lead.created_at), 'MMM dd, yyyy')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
